import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { PickedCompany } from "@/types";
import { debounce } from "lodash-es";
import { onBeforeUnmount, ref, watch } from "vue";
import { goSolidApi } from "./useGoSolidApi";

interface GlobalSearchCompany {
  id: number;
  name: string;
  business_id?: string;
  vat?: string;
  address?: string;
  emails?: string | null;
  country: string;
  account_eecs?: string;
  devices: GlobalSearchRegistryDevice[];
}

interface GlobalSearchRegistryDevice {
  id: number;
  code: string;
  name: string;
  code_type: string;
  capacity: number | null;
  technology_code: string | null;
  commercial_operation_date: string | null;
  support: boolean | null;
  country: string;
}

export function useGlobalCompanySearch(
  countryCode: string | null = null,
  limit = 100
) {
  const globalSearchCompanies = ref<GlobalSearchCompany[]>([]);
  const isLoading = ref(false);
  const hasError = ref(false);
  const searchQuery = ref("");

  const { globalErrorHandler } = useGlobalHandlers();

  // Fetch companies with search query from the API
  const fetchGlobalSearchCompanies = async (query = "") => {
    if (query && query.length < 2) {
      globalSearchCompanies.value = [];
      return;
    }

    isLoading.value = true;
    hasError.value = false;

    try {
      const response = await goSolidApi.get(`/api/utils/companies/`, {
        params: { search: query, country_code: countryCode, limit },
      });
      globalSearchCompanies.value = response.data;
    } catch (error) {
      globalErrorHandler(error);
      hasError.value = true;
      globalSearchCompanies.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  // Create a debounced version of the fetch function
  const debouncedFetchCompanies = debounce(fetchGlobalSearchCompanies, 500);

  // Watch for search query changes and perform search
  watch(searchQuery, (newQuery) => {
    debouncedFetchCompanies(newQuery);
  });

  // Update search query
  const updateGlobalCompanySearchQuery = (query: string) => {
    searchQuery.value = query;
  };

  const getCompanyUpdateById = (id: string): PickedCompany | undefined => {
    if (!id) return;

    const companyId = parseInt(id, 10);
    if (isNaN(companyId)) return;

    const selectedGlobalSearchCompany = globalSearchCompanies.value.find(
      (globalSearchCompany) => globalSearchCompany.id === companyId
    );

    if (!selectedGlobalSearchCompany) return;

    return {
      id: selectedGlobalSearchCompany.id,
      name: selectedGlobalSearchCompany.name,
      country: selectedGlobalSearchCompany.country,
      account_eecs: selectedGlobalSearchCompany.account_eecs || null,
      emails: selectedGlobalSearchCompany.emails || null,
    };
  };

  // Clean up to avoid memory leaks
  onBeforeUnmount(() => {
    debouncedFetchCompanies.cancel();
  });

  return {
    globalSearchCompanies,
    isLoading,
    hasError,
    updateGlobalCompanySearchQuery,
    getCompanyUpdateById,
  };
}
